import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { ListToolbar } from '~/components/layout';
import {
  Badge,
  Card,
  CardBody,
  Chip,
  ConfirmModal,
  DataTable,
  RowActionsMenu,
  type DataTableColumn,
} from '~/components/ui';
import { useDeleteAttributeMutation } from '~/modules/query/featureFlags';

import { OPERATOR_LABELS, isRequiredAttribute } from '~/components/Constants/featureFlagsConstants';
import { errorText, isStale, orDash } from '~/components/pages/feature-flags/common';
import type { Reference } from '~/components/pages/feature-flags/common';
import type { Attribute } from '~/components/pages/feature-flags/types';
import { routes } from '~/app/routes';

export const AttributesTab = ({ reference }: { reference: Reference }) => {
  const [deleting, setDeleting] = useState<Attribute | null>(null);
  const [deleteAttribute] = useDeleteAttributeMutation();

  const remove = (attribute: Attribute) => {
    deleteAttribute({ key: attribute.key, version: attribute.version })
      .unwrap()
      .then(() => toast.success('Attribute deleted'))
      .catch((err) =>
        toast.error(
          isStale(err)
            ? 'This attribute changed. Reload before deleting.'
            : errorText(
                err,
                'The attribute could not be deleted. It may still be used by targeting rules.',
              ),
        ),
      );
  };

  const columns: DataTableColumn<Attribute>[] = [
    {
      id: 'key',
      header: 'Attribute',
      cell: (a) => <span className="font-medium text-gray-800 dark:text-white/90">{a.key}</span>,
    },
    {
      id: 'notes',
      header: 'Notes',
      cell: (a) =>
        a.notes ? (
          <span className="block max-w-xs truncate" title={a.notes}>
            {a.notes}
          </span>
        ) : (
          orDash(null)
        ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (a) => (
        <Badge size="sm" color="gray">
          {a.type}
        </Badge>
      ),
    },
    {
      id: 'values',
      header: 'Allowed values',
      cell: (a) =>
        a.key === 'product' ? (
          'From products'
        ) : a.type === 'enum' && a.allowedValues?.length ? (
          <div className="flex max-w-xs flex-wrap gap-1.5">
            {a.allowedValues.map((v) => (
              <Chip key={v} label={v} />
            ))}
          </div>
        ) : (
          orDash(null)
        ),
    },
    {
      id: 'operators',
      header: 'Operators',
      cell: (a) => a.operators.map((op) => OPERATOR_LABELS[op]).join(', '),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: (a) => (
        <RowActionsMenu
          testId={`feature-flags-attributes-actions-${a.key}`}
          items={[
            {
              label: 'Edit',
              testId: 'edit',
              icon: <Pencil className="size-4" aria-hidden="true" />,
              href: routes.featureFlagsAdminRecord('attributes', a.key),
            },
            {
              label: 'Delete',
              testId: 'delete',
              destructive: true,
              disabled: isRequiredAttribute(a.key),
              icon: <Trash2 className="size-4" aria-hidden="true" />,
              onClick: () => setDeleting(a),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <Card>
        <ListToolbar
          title="Attributes"
          description="The context fields your apps send to target the right audience."
        />
        <CardBody className="p-0">
          <DataTable<Attribute>
            framed={false}
            testId="feature-flags-attributes-table"
            loading={reference.pending && reference.attributes.length === 0}
            emptyMessage="No attributes yet."
            emptyDescription={
              'Add a field your apps send in their context, then use it in rules. Keys are permanent.'
            }
            data={reference.attributes}
            rowKey={(a) => a.key}
            href={(a) => routes.featureFlagsAdminRecord('attributes', a.key)}
            rowLabel={(a) => `Open ${a.key}`}
            columns={columns}
          />
        </CardBody>
      </Card>

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete attribute"
        message={`Delete attribute '${deleting?.key}'?`}
        confirmLabel="Delete"
        variant="danger"
        dangerNote="Apps that still send this value are unaffected, but rules can no longer target it."
        testId="feature-flags-attributes-delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove(deleting);
          setDeleting(null);
        }}
      />
    </>
  );
};

export default AttributesTab;
